import type { RoadDiagramSpec } from './roadDiagram';

export const LEVEL_BANDS = ['starter', 'intermediate', 'advanced', 'graduation'] as const;

export type LevelBand = (typeof LEVEL_BANDS)[number];

export const LEVEL_LABELS: Record<LevelBand, { en: string; my: string }> = {
  starter: { en: 'Starter', my: 'အစ' },
  intermediate: { en: 'Intermediate', my: 'အလယ်အလတ်' },
  advanced: { en: 'Advanced', my: 'အဆင့်မြင့်' },
  graduation: { en: 'Graduation', my: 'ပြီးဆုံး' },
};

export interface PracticeChoice {
  text_en: string;
  text_my: string;
}

export interface PracticeQuestion {
  id: string;
  path_id: string;
  topic: string;
  prompt_en: string;
  prompt_my: string;
  choices: PracticeChoice[];
  correct_index: number;
  explanation_en: string;
  explanation_my: string;
  tip_en: string;
  tip_my: string;
  image_url?: string | null;
  diagram?: RoadDiagramSpec | null;
  caption_en?: string;
  caption_my?: string;
  status: 'draft' | 'published';
  sort_order: number;
}

export interface PathLessonRef {
  id: string;
  slug: string;
  title: string;
  titleMy?: string;
  titleEn?: string;
  hasCards: boolean;
}

export interface PathModuleRef {
  id: string;
  title: string;
  titleMy?: string;
  titleEn?: string;
  levelBand: LevelBand | null;
  lessons: PathLessonRef[];
}

export interface NumberedLesson extends PathLessonRef {
  number: number;
}

export interface LevelGroup {
  key: string;
  band: LevelBand | null;
  titleEn: string;
  titleMy: string;
  lessons: NumberedLesson[];
  completed: number;
  total: number;
}

export function isLevelBand(value: unknown): value is LevelBand {
  return value === 'starter' || value === 'intermediate' || value === 'advanced' || value === 'graduation';
}

/** Fallback length for this course's practice exam. Not a published DMV knowledge-test count. */
export const PRACTICE_EXAM_QUESTIONS = 36;

/** Fallback pass percent for this course's practice exam. Not an official score. */
export const PRACTICE_EXAM_PASS_PERCENT = 83;

export function clampExamCount(value: number): number {
  if (!Number.isFinite(value)) return PRACTICE_EXAM_QUESTIONS;
  return Math.min(100, Math.max(1, Math.round(value)));
}

export function clampPassPercent(value: number): number {
  if (!Number.isFinite(value)) return PRACTICE_EXAM_PASS_PERCENT;
  return Math.min(100, Math.max(1, Math.round(value)));
}

export function examSize(requested: number, available: number): { count: number; shortBank: boolean } {
  const want = clampExamCount(requested);
  if (available <= 0) return { count: 0, shortBank: true };
  return { count: Math.min(want, available), shortBank: available < want };
}

export function shortBankNote(requested: number, available: number): string | null {
  const size = examSize(requested, available);
  if (available <= 0) return 'This path has no practice questions yet.';
  if (!size.shortBank) return null;
  return `This practice exam has ${size.count} questions because the bank has ${size.count}, not ${clampExamCount(requested)}.`;
}

export function passedExam(percent: number, threshold: number): boolean {
  return percent >= clampPassPercent(threshold);
}

export function readinessFromScores(percents: number[]): number | null {
  if (percents.length === 0) return null;
  const recent = percents.slice(-3);
  const total = recent.reduce((sum, value) => sum + value, 0);
  return Math.round(total / recent.length);
}

/** A gap longer than this starts a new topic run for the last-score percent. */
export const TOPIC_RUN_GAP_MS = 20 * 60 * 1000;

export interface TopicStudySummary {
  percentLearned: number | null;
  practiceCount: number;
  lastScore: number | null;
}

export function topicStudySummary(questionIds: string[], attempts: AttemptRef[]): TopicStudySummary {
  const ids = new Set(questionIds);
  const relevant = attempts.filter((attempt) => ids.has(attempt.questionId));
  if (relevant.length === 0 || questionIds.length === 0) {
    return { percentLearned: null, practiceCount: 0, lastScore: null };
  }
  const latest = new Map<string, AttemptRef>();
  for (const attempt of relevant) {
    const previous = latest.get(attempt.questionId);
    if (!previous || attempt.createdAt >= previous.createdAt) latest.set(attempt.questionId, attempt);
  }
  const learned = questionIds.filter((id) => latest.get(id)?.correct).length;
  const ordered = [...relevant].sort((left, right) => left.createdAt.localeCompare(right.createdAt));
  let start = ordered.length - 1;
  for (let index = ordered.length - 1; index > 0; index -= 1) {
    const gap = Date.parse(ordered[index].createdAt) - Date.parse(ordered[index - 1].createdAt);
    if (!Number.isFinite(gap) || gap > TOPIC_RUN_GAP_MS) break;
    start = index - 1;
  }
  const run = ordered.slice(start);
  const correct = run.filter((attempt) => attempt.correct).length;
  return {
    percentLearned: Math.round((learned / questionIds.length) * 100),
    practiceCount: relevant.length,
    lastScore: Math.round((correct / run.length) * 100),
  };
}

export function localDateKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function previousLocalDate(today: string): string {
  const [year, month, day] = today.split('-').map(Number);
  const date = new Date(year, (month || 1) - 1, day || 1);
  date.setDate(date.getDate() - 1);
  return localDateKey(date);
}

export function nextStreak(
  current: number,
  lastDate: string | null,
  today: string
): { current: number; changed: boolean } {
  const safeCurrent = Number.isFinite(current) ? Math.max(0, current) : 0;
  if (lastDate === today) return { current: safeCurrent, changed: false };
  if (lastDate && previousLocalDate(today) === lastDate) {
    return { current: safeCurrent + 1, changed: true };
  }
  return { current: 1, changed: true };
}

export interface AttemptRef {
  questionId: string;
  correct: boolean;
  createdAt: string;
}

export function missedQuestionIds(attempts: AttemptRef[]): string[] {
  const latest = new Map<string, AttemptRef>();
  for (const attempt of attempts) {
    const previous = latest.get(attempt.questionId);
    if (!previous || attempt.createdAt >= previous.createdAt) latest.set(attempt.questionId, attempt);
  }
  return [...latest.entries()].filter(([, attempt]) => !attempt.correct).map(([id]) => id);
}

export function hardestQuestionIds(attempts: { questionId: string; correct: boolean }[]): string[] {
  const misses = new Map<string, number>();
  for (const attempt of attempts) {
    if (!attempt.correct) misses.set(attempt.questionId, (misses.get(attempt.questionId) || 0) + 1);
  }
  return [...misses.entries()].sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0])).map(([id]) => id);
}

function numbered(groups: Omit<LevelGroup, 'lessons' | 'completed' | 'total'> & { lessons: PathLessonRef[] }[], completedIds: Set<string>): LevelGroup[] {
  let number = 1;
  return groups.map((group) => {
    const lessons = group.lessons.map((lesson) => ({ ...lesson, number: number++ }));
    const completed = lessons.filter((lesson) => completedIds.has(lesson.id)).length;
    return { ...group, lessons, completed, total: lessons.length };
  });
}

export function groupLessonsByLevel(modules: PathModuleRef[], completedIds: Set<string>): LevelGroup[] {
  const anyBand = modules.some((module) => isLevelBand(module.levelBand));
  if (!anyBand) {
    return numbered(
      modules.map((module) => ({
        key: module.id,
        band: null,
        titleEn: module.titleEn || module.title,
        titleMy: module.titleMy || module.title,
        lessons: module.lessons,
      })),
      completedIds
    );
  }

  const buckets = new Map<LevelBand, PathLessonRef[]>();
  const unbanded: { key: string; band: null; titleEn: string; titleMy: string; lessons: PathLessonRef[] }[] = [];
  for (const module of modules) {
    if (isLevelBand(module.levelBand)) {
      const list = buckets.get(module.levelBand) || [];
      list.push(...module.lessons);
      buckets.set(module.levelBand, list);
    } else if (module.lessons.length > 0) {
      unbanded.push({
        key: module.id,
        band: null,
        titleEn: module.titleEn || module.title,
        titleMy: module.titleMy || module.title,
        lessons: module.lessons,
      });
    }
  }

  const banded = LEVEL_BANDS.filter((band) => (buckets.get(band) || []).length > 0).map((band) => ({
    key: band,
    band,
    titleEn: LEVEL_LABELS[band].en,
    titleMy: LEVEL_LABELS[band].my,
    lessons: buckets.get(band) || [],
  }));
  return numbered([...banded, ...unbanded], completedIds);
}

export function pickExamQuestions<T>(questions: T[], requested: number): { questions: T[]; shortBank: boolean; count: number } {
  const size = examSize(requested, questions.length);
  const copy = [...questions];
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    const current = copy[index];
    copy[index] = copy[swapIndex];
    copy[swapIndex] = current;
  }
  return { questions: copy.slice(0, size.count), shortBank: size.shortBank, count: size.count };
}
